/**
 * SDK-neutral agent tool declarations (single source of truth).
 *
 * Plain-JSON schemas for the agent tools, without depending on any GenAI SDK
 * (shared rule: types + pure constants only). Consumers translate these into
 * their SDK's shape: `functions/src/agent/tools.ts` converts them into
 * @google/genai FunctionDeclarations for the text chat loop, and the web voice
 * session (Firebase AI Logic) converts them for the Gemini Live API.
 */

import type { AgentToolName } from './agent'

/** SDK-neutral JSON-schema subset used by the tool declarations. */
export interface NeutralSchema {
  type: 'object' | 'string' | 'number' | 'boolean' | 'array'
  description?: string
  enum?: string[]
  properties?: Record<string, NeutralSchema>
  required?: string[]
  items?: NeutralSchema
}

/** SDK-neutral function declaration for one agent tool. */
export interface NeutralFunctionDeclaration {
  name: AgentToolName
  description: string
  parameters?: NeutralSchema
}

/**
 * Declarations for every dispatchable agent tool. Names must match
 * AGENT_TOOL_NAMES and the functions-side dispatcher (tools.ts#executeTool).
 */
export const AGENT_TOOL_DECLARATIONS: readonly NeutralFunctionDeclaration[] = [
  {
    name: 'getCalendar',
    description:
      'Consulta a agenda (Outlook) do usuário. Informe um intervalo (startISO/endISO em ISO 8601) OU um preset. Se nada for informado, usa o dia de hoje.',
    parameters: {
      type: 'object',
      properties: {
        startISO: {
          type: 'string',
          description: 'Início do intervalo em ISO 8601 (inclusivo).',
        },
        endISO: {
          type: 'string',
          description: 'Fim do intervalo em ISO 8601 (exclusivo).',
        },
        preset: {
          type: 'string',
          enum: ['today', 'tomorrow', 'thisWeek', 'next24h'],
          description: 'Atalho de intervalo, alternativa a startISO/endISO.',
        },
      },
    },
  },
  {
    name: 'createReminder',
    description: 'Cria um lembrete para o usuário, entregue via Teams no vencimento.',
    parameters: {
      type: 'object',
      properties: {
        title: { type: 'string', description: 'Título curto do lembrete.' },
        dueISO: {
          type: 'string',
          description: 'Data e hora de vencimento em ISO 8601.',
        },
        description: {
          type: 'string',
          description: 'Detalhes opcionais do lembrete.',
        },
        urgent: {
          type: 'boolean',
          description:
            'Marca o lembrete como urgente (ênfase no texto composto); não altera os canais de entrega.',
        },
      },
      required: ['title', 'dueISO'],
    },
  },
  {
    name: 'listReminders',
    description: 'Lista os lembretes do usuário, com filtros opcionais de status e intervalo.',
    parameters: {
      type: 'object',
      properties: {
        status: {
          type: 'string',
          enum: ['scheduled', 'sent', 'done', 'snoozed', 'cancelled'],
          description: 'Filtra por status do lembrete.',
        },
        fromISO: { type: 'string', description: 'Vencimento mínimo em ISO 8601.' },
        toISO: { type: 'string', description: 'Vencimento máximo em ISO 8601.' },
      },
    },
  },
  {
    name: 'sendTeamsMessage',
    description: 'Envia uma mensagem de texto ao usuário no Microsoft Teams.',
    parameters: {
      type: 'object',
      properties: {
        text: { type: 'string', description: 'Conteúdo da mensagem em texto.' },
      },
      required: ['text'],
    },
  },
  {
    name: 'getClientProfile',
    description: 'Busca o perfil de um cliente por nome, empresa ou palavra-chave.',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: 'Nome, empresa ou termo de busca do cliente.',
        },
      },
      required: ['query'],
    },
  },
  {
    name: 'upsertClientProfile',
    description: 'Cria ou atualiza o perfil de um cliente.',
    parameters: {
      type: 'object',
      properties: {
        name: { type: 'string', description: 'Nome do cliente.' },
        company: { type: 'string', description: 'Empresa do cliente.' },
        sector: { type: 'string', description: 'Setor de atuação.' },
        notes: { type: 'string', description: 'Anotações livres sobre o cliente.' },
        matchKeywords: {
          type: 'array',
          items: { type: 'string' },
          description: 'Palavras-chave para associar o cliente a eventos da agenda.',
        },
      },
      required: ['name'],
    },
  },
  {
    name: 'webSearch',
    description:
      'Pesquisa na web (Google) por fatos externos e recentes: notícias, decisões e jurisprudência públicas, indicadores econômicos, mudanças legislativas. NÃO use para agenda, lembretes ou dados de clientes — use as ferramentas específicas. Retorna um resumo factual e a lista de fontes.',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: 'Pergunta ou termos de busca, de preferência em português.',
        },
      },
      required: ['query'],
    },
  },
  {
    name: 'searchMemories',
    description:
      'Busca nas memórias APROVADAS do usuário por texto, categoria ou assunto. Use quando precisar de detalhes que vão além do resumo de memórias já presente no contexto.',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: 'Texto livre para buscar no conteúdo das memórias.',
        },
        category: {
          type: 'string',
          enum: ['user_preference', 'person', 'work_context'],
          description: 'Filtra por categoria da memória.',
        },
        subject: {
          type: 'string',
          description: 'Filtra pelo assunto da memória (ex.: nome da pessoa).',
        },
      },
    },
  },
  {
    name: 'saveMemory',
    description:
      'Salva uma memória persistente sobre o usuário. REGRA FIRME: use EXCLUSIVAMENTE quando o usuário pedir de forma direta e explícita que algo seja lembrado ("lembre que...", "anote que...", "guarde isso"). NUNCA use proativamente, mesmo que a informação pareça valiosa — a consolidação automática de memórias é feita por um processo diário separado.',
    parameters: {
      type: 'object',
      properties: {
        content: {
          type: 'string',
          description: 'Fato autocontido em UMA frase, em português.',
        },
        category: {
          type: 'string',
          enum: ['user_preference', 'person', 'work_context'],
          description: 'Categoria da memória.',
        },
        subject: {
          type: 'string',
          description:
            'Assunto da memória (ex.: nome da pessoa). Obrigatório quando category é person.',
        },
      },
      required: ['content', 'category'],
    },
  },
  {
    name: 'completeOnboarding',
    description:
      'Use SOMENTE ao final da apresentação inicial (onboarding) para salvar as memórias coletadas e concluir a apresentação. Aceita uma lista vazia se o usuário preferir pular.',
    parameters: {
      type: 'object',
      properties: {
        memories: {
          type: 'array',
          description: 'Memórias coletadas durante a apresentação (pode ser vazia).',
          items: {
            type: 'object',
            properties: {
              category: {
                type: 'string',
                enum: ['user_preference', 'person', 'work_context'],
                description: 'Categoria da memória.',
              },
              subject: {
                type: 'string',
                description: 'Assunto da memória. Obrigatório quando category é person.',
              },
              content: {
                type: 'string',
                description: 'Fato autocontido em UMA frase, em português.',
              },
            },
            required: ['category', 'content'],
          },
        },
      },
      required: ['memories'],
    },
  },
  {
    name: 'startEmailResearch',
    description:
      'Inicia uma pesquisa PROFUNDA nos e-mails do usuário (operação custosa). Lê os e-mails do período, classifica por criticidade e monta um relatório executivo. Roda em segundo plano: o relatório aparece nesta mesma conversa quando ficar pronto. Antes de chamar, colete o período (obrigatório) e o foco da análise; filtros de remetente/assunto e o envio por e-mail são opcionais. Use apenas quando o usuário pedir uma análise ampla dos e-mails — para leituras pontuais não use esta ferramenta. Há um limite de 1000 e-mails por pesquisa: se o período informado tiver um volume estimado acima disso, a ferramenta recusa a chamada (sem criar nada) e devolve uma orientação pedindo para restringir o período ou adicionar filtros de remetente/assunto — repasse essa orientação ao usuário em vez de tentar contornar. Caixas muito movimentadas (centenas de e-mails por dia) devem ser pesquisadas em intervalos curtos, de um a dois dias por vez.',
    parameters: {
      type: 'object',
      properties: {
        startISO: {
          type: 'string',
          description: 'Início do período em ISO 8601 (inclusivo).',
        },
        endISO: {
          type: 'string',
          description: 'Fim do período em ISO 8601 (inclusivo).',
        },
        focus: {
          type: 'string',
          description: 'Foco da análise: o que priorizar (ex.: prazos, um cliente, um tema).',
        },
        allowedSenders: {
          type: 'array',
          items: { type: 'string' },
          description: 'Opcional. Restringe aos e-mails destes remetentes (endereços).',
        },
        subjectFilter: {
          type: 'string',
          description: 'Opcional. Termo que deve estar contido no assunto.',
        },
        excludeRecipients: {
          type: 'array',
          items: { type: 'string' },
          description:
            'Opcional. Exclui e-mails ENDEREÇADOS a estes endereços (to/cc); NÃO filtra por remetente.',
        },
        sendEmail: {
          type: 'boolean',
          description:
            'Opcional. Se verdadeiro, também envia o relatório por e-mail ao usuário. Recurso sujeito a habilitação pelo administrador: se estiver desativado, o pedido é ignorado e o relatório chega apenas na conversa.',
        },
      },
      required: ['startISO', 'endISO', 'focus'],
    },
  },
  {
    name: 'searchEmails',
    description:
      'Busca PONTUAL nos e-mails do usuário (leitura rápida, sem confirmação): localiza mensagens por termo no assunto, remetente e/ou período e retorna id, remetente, assunto, data, prévia e link de cada uma. Use o campo id do resultado como messageId em replyEmail. Para análise ampla/relatório de um período use startEmailResearch, não esta ferramenta.',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: 'Opcional. Termo contido no ASSUNTO das mensagens.',
        },
        from: {
          type: 'string',
          description: 'Opcional. Remetente: endereço exato ou parte do nome/endereço.',
        },
        startISO: {
          type: 'string',
          description: 'Opcional. Início do período de recebimento em ISO 8601 (inclusivo).',
        },
        endISO: {
          type: 'string',
          description: 'Opcional. Fim do período de recebimento em ISO 8601 (inclusivo).',
        },
        limit: {
          type: 'number',
          description: 'Opcional. Máximo de resultados (padrão 10, teto 25).',
        },
      },
    },
  },
  {
    name: 'sendEmail',
    description:
      'Envia um NOVO e-mail em nome do usuário pelo Outlook. Ação de escrita: será apresentado um cartão de confirmação e o e-mail só sai após o usuário confirmar. Colete destinatário(s), assunto e corpo antes de chamar. Use apenas quando o usuário pedir para enviar um e-mail.',
    parameters: {
      type: 'object',
      properties: {
        to: {
          type: 'array',
          items: { type: 'string' },
          description: 'Destinatários (endereços de e-mail). Ao menos um.',
        },
        cc: {
          type: 'array',
          items: { type: 'string' },
          description: 'Opcional. Destinatários em cópia (endereços de e-mail).',
        },
        subject: { type: 'string', description: 'Assunto do e-mail.' },
        body: { type: 'string', description: 'Corpo do e-mail em texto simples.' },
        accountId: {
          type: 'string',
          description:
            "Identificador da conta de origem quando o usuário tem mais de uma conta conectada: 'primary' (conta Microsoft principal), 'google-1' ou 'ms-extra-1'..'ms-extra-3'. Omitir quando houver apenas uma conta.",
        },
      },
      required: ['to', 'subject', 'body'],
    },
  },
  {
    name: 'replyEmail',
    description:
      'Responde a um e-mail EXISTENTE do usuário (identificado por messageId) pelo Outlook. Obtenha o messageId com a ferramenta searchEmails (campo id de cada resultado) — nunca invente um id. Ação de escrita: será apresentado um cartão de confirmação e a resposta só sai após o usuário confirmar. Use apenas quando o usuário pedir para responder a uma mensagem específica.',
    parameters: {
      type: 'object',
      properties: {
        messageId: {
          type: 'string',
          description:
            'Id da mensagem do Outlook a ser respondida (campo id retornado por searchEmails).',
        },
        body: { type: 'string', description: 'Corpo da resposta em texto simples.' },
        replyAll: {
          type: 'boolean',
          description: 'Opcional. Responder a todos os destinatários (default: só ao remetente).',
        },
        accountId: {
          type: 'string',
          description:
            "Identificador da conta de origem quando o usuário tem mais de uma conta conectada: 'primary' (conta Microsoft principal), 'google-1' ou 'ms-extra-1'..'ms-extra-3'. Omitir quando houver apenas uma conta.",
        },
      },
      required: ['messageId', 'body'],
    },
  },
  {
    name: 'createEvent',
    description:
      'Cria um evento na agenda (Outlook) do usuário, opcionalmente como reunião online do Teams (padrão). Ação de escrita: será apresentado um cartão de confirmação e o evento só é criado após o usuário confirmar. Colete título, início e fim antes de chamar; participantes, local e pauta são opcionais. Quando criar reunião do Teams, o link de entrada é retornado. Use apenas quando o usuário pedir para agendar/marcar um evento ou reunião. Use a data e hora atuais fornecidas no contexto como referência para calcular início/fim e NUNCA agende um evento no passado.',
    parameters: {
      type: 'object',
      properties: {
        subject: { type: 'string', description: 'Título do evento.' },
        startISO: {
          type: 'string',
          description:
            'Início do evento em ISO 8601 no fuso do usuário, SEMPRE com o offset (ex.: 2026-07-14T15:00:00-03:00). Nunca use "Z" nem omita o fuso.',
        },
        endISO: {
          type: 'string',
          description:
            'Fim do evento em ISO 8601 no fuso do usuário, SEMPRE com o offset (ex.: 2026-07-14T16:00:00-03:00). Nunca use "Z" nem omita o fuso.',
        },
        attendees: {
          type: 'array',
          items: { type: 'string' },
          description: 'Opcional. Participantes a convidar (endereços de e-mail).',
        },
        body: {
          type: 'string',
          description: 'Opcional. Descrição/pauta do evento em texto simples.',
        },
        location: {
          type: 'string',
          description: 'Opcional. Local do evento (sala, endereço).',
        },
        isOnlineMeeting: {
          type: 'boolean',
          description:
            'Opcional. Criar como reunião online do Teams (default true). Use false para um compromisso sem link do Teams.',
        },
        accountId: {
          type: 'string',
          description:
            "Identificador da conta de origem quando o usuário tem mais de uma conta conectada: 'primary' (conta Microsoft principal), 'google-1' ou 'ms-extra-1'..'ms-extra-3'. Omitir quando houver apenas uma conta.",
        },
      },
      required: ['subject', 'startISO', 'endISO'],
    },
  },
  {
    name: 'searchTenantUsers',
    description:
      'Pesquisa pessoas no diretório da organização (colegas do escritório) por nome ou e-mail e retorna id, nome, e-mail, cargo e departamento de cada uma. Leitura rápida, sem confirmação. Use para encontrar o e-mail de um colega antes de convidá-lo a um evento ou de enviar/responder um e-mail.',
    parameters: {
      type: 'object',
      properties: {
        query: {
          type: 'string',
          description: 'Termo de busca por nome ou e-mail da pessoa.',
        },
        limit: {
          type: 'number',
          description: 'Opcional. Máximo de resultados (padrão 10, teto 25).',
        },
      },
      required: ['query'],
    },
  },
  {
    name: 'listConnectedAccounts',
    description:
      'Lista as contas de e-mail e agenda conectadas (a conta principal do Outlook e as contas adicionais — Google e/ou Microsoft extra). Use para descobrir o "id" de conta a passar em accountId (sendEmail/replyEmail/createEvent) quando houver mais de uma conta com a mesma capacidade, ou quando o usuário perguntar quais contas estão conectadas.',
    parameters: { type: 'object', properties: {} },
  },
  {
    name: 'sendWhatsappMessage',
    description:
      'BETA. Envia uma mensagem de texto pelo WhatsApp SEMPRE para o número vinculado do PRÓPRIO usuário — nunca para terceiros (não existe parâmetro de destinatário). Ação de escrita: será apresentado um cartão de confirmação e a mensagem só sai após o usuário confirmar. Exige que o usuário já tenha vinculado um número de WhatsApp em Configurações; se não tiver, ou se o canal não estiver habilitado, a ferramenta devolve um erro explicando o que fazer. Se a janela de 24h de mensagens livres estiver aberta, o texto é entregue como está; se estiver fechada, a mensagem é entregue por um template aprovado (o texto vira o conteúdo da notificação) — não é um erro. Use apenas quando o usuário pedir explicitamente para receber algo pelo WhatsApp.',
    parameters: {
      type: 'object',
      properties: {
        text: { type: 'string', description: 'Conteúdo da mensagem em texto.' },
      },
      required: ['text'],
    },
  },
  {
    name: 'sendWhatsappTemplateToContact',
    description:
      'BETA. Dispara o template de WhatsApp aprovado da firma para um CONTATO DO CRM (terceiro), nunca para um número avulso. Informe contactQuery (nome, telefone ou e-mail do contato) — NUNCA um id: o backend localiza o contato no CRM a partir desse termo. Se a busca encontrar mais de um contato, a ferramenta devolve os candidatos para você perguntar ao usuário qual é o correto e chamar de novo com uma busca mais específica — nunca escolha um contato sozinho. O texto (text) vira o conteúdo do template aprovado (nunca é enviado como texto livre). Ação de escrita: será apresentado um cartão de confirmação com o nome e o telefone (mascarado) do destinatário, e o envio só ocorre após o usuário confirmar. Exige acesso liberado pelo administrador. Use apenas quando o usuário pedir explicitamente para notificar um contato/cliente pelo WhatsApp.',
    parameters: {
      type: 'object',
      properties: {
        contactQuery: {
          type: 'string',
          description: 'Nome, telefone ou e-mail para localizar o contato no CRM.',
        },
        text: {
          type: 'string',
          description: 'Conteúdo que preenche a variável do template aprovado.',
        },
      },
      required: ['contactQuery', 'text'],
    },
  },
]
